import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-zinc-100 text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-900 font-medium text-right">{value}</span>
    </div>
  );
}

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) setEmpty(false);
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setEmpty(true);
    onChange("");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={520}
        height={180}
        data-testid="signature-canvas"
        className="w-full border-2 border-dashed border-zinc-300 rounded-lg bg-zinc-50 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-zinc-400">{empty ? "Signez dans le cadre ci-dessus" : "Signature saisie ✓"}</span>
        <button onClick={clear} data-testid="clear-signature-btn" className="text-xs text-zinc-500 hover:text-red-500 underline">Effacer</button>
      </div>
    </div>
  );
}

export default function SignerBailPublic() {
  const { token } = useParams();
  const [bail, setBail] = useState(null);
  const [error, setError] = useState("");
  const [sig, setSig] = useState("");
  const [accepte, setAccepte] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/baux/${token}`)
      .then((r) => {
        setBail(r.data.bail);
        if (r.data.bail.has_signature_locataire) setDone(true);
      })
      .catch(() => setError("Lien invalide ou expiré. Contactez votre propriétaire."));
  }, [token]);

  async function submit() {
    if (!accepte) return toast.error("Veuillez confirmer avoir lu et accepté les conditions.");
    if (!sig) return toast.error("Veuillez signer dans le cadre.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/baux/${token}/signer`, { signature_data: sig, accepte_conditions: accepte });
      setDone(true);
      toast.success("Bail signé avec succès !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la signature.");
    }
    setSubmitting(false);
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="text-center text-zinc-300" data-testid="bail-error">
          <div className="text-5xl mb-3">⚠️</div>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  if (!bail) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400">Chargement…</div>;
  }

  const { locateur, locataire, logement, bail: b, loyer } = bail;

  return (
    <div className="min-h-screen bg-zinc-950 py-8 px-4" data-testid="signer-bail-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <div className="text-amber-500 text-xs uppercase tracking-widest font-semibold">KAIZEN System · Bail de logement</div>
          <h1 className="text-2xl font-bold text-white mt-1">Signature électronique de votre bail</h1>
          <p className="text-zinc-400 text-sm mt-1">Logement : {logement?.adresse}, {logement?.ville}</p>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-xl">
          <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-2">Récapitulatif</h2>
          <Row label="Locateur" value={locateur?.nom} />
          <Row label="Locataire" value={locataire?.nom} />
          <Row label="Logement" value={`${logement?.adresse || ""} ${logement?.app ? "app. " + logement.app : ""}`} />
          <Row label="Municipalité" value={`${logement?.ville || ""} ${logement?.code_postal || ""}`} />
          <Row label="Type de bail" value={b?.type === "fixe" ? "Durée fixe" : "Durée indéterminée"} />
          <Row label="Début" value={b?.date_debut} />
          <Row label="Fin" value={b?.date_fin} />
          <Row label="Loyer" value={loyer?.montant ? `${loyer.montant} $ / ${loyer.frequence || "mois"}` : null} />
          <Row label="Paiement" value={loyer?.jour_paiement} />
          {bail?.clauses_speciales ? (
            <div className="mt-3 text-xs text-zinc-600 bg-zinc-50 rounded p-3">
              <div className="font-semibold text-zinc-700 mb-1">Clauses particulières</div>
              <div className="whitespace-pre-wrap">{bail.clauses_speciales}</div>
            </div>
          ) : null}

          {done ? (
            <div className="mt-6 text-center py-6" data-testid="bail-signed-confirm">
              <div className="text-4xl mb-2">✅</div>
              <p className="text-zinc-900 font-semibold">Bail signé. Merci !</p>
              <p className="text-zinc-500 text-sm mt-1">Votre propriétaire en a été informé. Vous recevrez une copie.</p>
            </div>
          ) : (
            <div className="mt-6">
              <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wide mb-2">Votre signature</h2>
              <SignaturePad onChange={setSig} />
              <label className="flex items-start gap-2 mt-4 cursor-pointer" data-testid="accepte-checkbox-label">
                <input
                  type="checkbox"
                  checked={accepte}
                  onChange={(e) => setAccepte(e.target.checked)}
                  data-testid="accepte-conditions-checkbox"
                  className="mt-0.5 w-4 h-4 accent-amber-500"
                />
                <span className="text-xs text-zinc-600">
                  J'ai lu et j'accepte les conditions du présent bail. Je comprends que ma signature,
                  horodatée et associée à mon adresse IP, est conservée à titre de preuve.
                </span>
              </label>
              <button
                onClick={submit}
                disabled={submitting || !accepte || !sig}
                data-testid="submit-signature-btn"
                className="mt-4 w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-950 font-semibold py-3 rounded-lg transition-colors"
              >
                {submitting ? "Envoi…" : "Signer le bail"}
              </button>
            </div>
          )}
        </div>
        <p className="text-center text-zinc-600 text-xs mt-4">Institut Impact Élite Martial · KAIZEN System</p>
      </div>
    </div>
  );
}
